import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Spin, Empty, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#B8892A';
const GOLD_L = '#C8A84E';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const CARD_BG = '#fff';

const CATEGORIES = ['推荐', '腕表', '珠宝', '箱包', '配饰', '服装'];

const BRANDS_MAP = {
  '推荐': ['Rolex', 'Hermès', 'Cartier', 'Louis Vuitton', 'Chanel', 'Tiffany', 'Omega', 'BVLGARI', 'Dior', 'Patek Philippe'],
  '腕表': ['Rolex', 'Omega', 'Cartier', 'Patek Philippe', 'Audemars Piguet', 'IWC', 'Breitling', 'TAG Heuer', 'Panerai', 'Grand Seiko'],
  '珠宝': ['Cartier', 'Tiffany & Co.', 'Van Cleef & Arpels', 'BVLGARI', 'Harry Winston', 'Buccellati', 'Mikimoto', 'Chopard'],
  '箱包': ['Hermès', 'Louis Vuitton', 'Chanel', 'Gucci', 'CELINE', 'Dior', 'Prada', 'Bottega Veneta', 'LOEWE', 'Goyard'],
  '配饰': ['Hermès', 'Cartier', 'Chanel', 'Louis Vuitton', 'Gucci', 'CELINE', 'Chrome Hearts'],
  '服装': ['Chanel', 'Dior', 'Louis Vuitton', 'Gucci', 'Prada', 'Burberry', 'Max Mara'],
};

const SCENE_TAGS = [
  { key: 'auction_source', label: '日拍直供', icon: '🇯🇵' },
  { key: 'near_new',       label: '近新微瑕', icon: '✨' },
  { key: 'under_10k',      label: '万元以下', icon: '💰' },
  { key: 'popular',        label: '人气 Top', icon: '🔥' },
  { key: 'exhibition',     label: '展会特供', icon: '🏛' },
];

const RANKING_CATS = ['腕表', '珠宝', '箱包', '配饰', '服装'];

const GRADE_COLORS = {
  S: '#8C1F1F', A: '#B8892A', B: '#5C7A3A', C: '#666',
};

const GRADE_OPTIONS = [
  { key: 'S', label: 'S · 未使用' },
  { key: 'A', label: 'A · 近新' },
  { key: 'B', label: 'B · 良好' },
  { key: 'C', label: 'C · 有瑕疵' },
];

export default function Market() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  const [activeCat, setActiveCat] = useState(searchParams.get('category') || '推荐');
  const [activeBrand, setActiveBrand] = useState(searchParams.get('brand') || '');
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState(searchParams.get('tag') || '');
  const [sort, setSort] = useState('newest');

  // Filter state
  const [activeGrades, setActiveGrades] = useState([]);
  const [pricingMode, setPricingMode] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  // Filter modal state
  const [filterOpen, setFilterOpen] = useState(false);
  const [tmpGrades, setTmpGrades] = useState([]);
  const [tmpPricingMode, setTmpPricingMode] = useState('');
  const [tmpSourceType, setTmpSourceType] = useState('');
  const [tmpPriceMin, setTmpPriceMin] = useState('');
  const [tmpPriceMax, setTmpPriceMax] = useState('');
  const [tmpBrand, setTmpBrand] = useState('');
  const [tmpCat, setTmpCat] = useState('');

  // Quick filter dropdowns
  const [gradeDropOpen, setGradeDropOpen] = useState(false);
  const [brandDropOpen, setBrandDropOpen] = useState(false);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [rankings, setRankings] = useState([]);
  const [rankCat, setRankCat] = useState('腕表');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    const handler = () => { setGradeDropOpen(false); setBrandDropOpen(false); };
    if (gradeDropOpen || brandDropOpen) {
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [gradeDropOpen, brandDropOpen]);

  const fetchItems = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50, sort };
      if (activeCat !== '推荐') params.category = activeCat;
      if (activeBrand) params.brand = activeBrand;
      if (search) params.search = search;
      if (activeTag) params.tag = activeTag;
      if (activeGrades.length > 0) params.grade = activeGrades.join(',');
      if (pricingMode) params.pricing_mode = pricingMode;
      if (sourceType) params.source_type = sourceType;
      if (priceMin) params.price_min = priceMin;
      if (priceMax) params.price_max = priceMax;
      const res = await api.get('/market', { params });
      if (p === 1) setItems(res.data.data);
      else setItems(prev => [...prev, ...res.data.data]);
      setTotal(res.data.total);
      setPage(p);
    } catch (e) {
      message.error(t('market.loadFailed'));
    } finally { setLoading(false); }
  }, [activeCat, activeBrand, search, activeTag, sort, activeGrades, pricingMode, sourceType, priceMin, priceMax]);

  useEffect(() => { fetchItems(1); }, [fetchItems]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/market/rankings', { params: { sub_category: rankCat, limit: 5 } });
        setRankings(res.data.data || []);
      } catch { setRankings([]); }
    })();
  }, [rankCat]);

  const px = isMobile ? 16 : 32;
  const brands = BRANDS_MAP[activeCat] || BRANDS_MAP['推荐'];

  const handleCatChange = (cat) => {
    setActiveCat(cat);
    setActiveBrand('');
    setActiveTag('');
  };

  const coverUrl = (item) => {
    if (item.photos?.length > 0) return item.photos[0];
    return `https://placehold.co/400x400/F5EDD8/B8892A?text=${encodeURIComponent(item.brand || item.item_name?.slice(0, 2) || '?')}`;
  };

  const activeFilterCount = activeGrades.length + (pricingMode ? 1 : 0) + (sourceType ? 1 : 0)
    + (priceMin ? 1 : 0) + (priceMax ? 1 : 0);

  const openFilterModal = () => {
    setTmpGrades([...activeGrades]);
    setTmpPricingMode(pricingMode);
    setTmpSourceType(sourceType);
    setTmpPriceMin(priceMin);
    setTmpPriceMax(priceMax);
    setTmpBrand(activeBrand);
    setTmpCat(activeCat);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    setActiveGrades(tmpGrades);
    setPricingMode(tmpPricingMode);
    setSourceType(tmpSourceType);
    setPriceMin(tmpPriceMin);
    setPriceMax(tmpPriceMax);
    setActiveBrand(tmpBrand);
    if (tmpCat !== activeCat) setActiveCat(tmpCat);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setTmpGrades([]);
    setTmpPricingMode('');
    setTmpSourceType('');
    setTmpPriceMin('');
    setTmpPriceMax('');
    setTmpBrand('');
    setTmpCat(activeCat);
  };

  const clearAllFilters = () => {
    setActiveGrades([]);
    setPricingMode('');
    setSourceType('');
    setPriceMin('');
    setPriceMax('');
  };

  const toggleGrade = (g) => {
    setActiveGrades(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };
  const toggleTmpGrade = (g) => {
    setTmpGrades(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const chipStyle = (active) => ({
    padding: '6px 14px', borderRadius: 16, border: `1px solid ${active ? GOLD : BD}`,
    background: active ? 'rgba(184,137,42,.1)' : '#fff', color: active ? GOLD : INK,
    fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: active ? 500 : 400,
  });

  const filterChipStyle = (active) => ({
    padding: '8px 16px', borderRadius: 6, border: `1px solid ${active ? GOLD : BD}`,
    background: active ? 'rgba(184,137,42,.08)' : '#fff', color: active ? GOLD : INK,
    fontSize: 13, cursor: 'pointer', fontWeight: active ? 500 : 400,
  });

  return (
    <div style={{ background: BG, minHeight: '100vh' }}>
      {/* SEARCH BAR */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BD}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: `16px ${px}px 0` }}>
          <Input.Search
            placeholder={t('market.searchPlaceholder')}
            allowClear
            onSearch={v => setSearch(v)}
            style={{ maxWidth: 600, marginBottom: 12 }}
            size="large"
          />

          {/* Category Tabs */}
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto', marginBottom: 0 }}>
            {CATEGORIES.map(cat => {
              const active = activeCat === cat;
              return (
                <button key={cat} onClick={() => handleCatChange(cat)} style={{
                  padding: '8px 18px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: active ? 700 : 400,
                  color: active ? GOLD : MUTED, background: 'transparent',
                  borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                  whiteSpace: 'nowrap', transition: 'all .2s',
                }}>{cat}</button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ padding: `0 ${px}px`, maxWidth: 1200, margin: '0 auto' }}>
        {/* BRAND STRIP */}
        <div style={{ padding: '16px 0', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 12, minWidth: 'max-content' }}>
            {brands.map(b => {
              const active = activeBrand === b;
              return (
                <button key={b} onClick={() => setActiveBrand(active ? '' : b)} style={{
                  padding: '8px 16px', borderRadius: 20, border: `1px solid ${active ? GOLD : BD}`,
                  background: active ? 'rgba(184,137,42,.1)' : '#fff', color: active ? GOLD : INK,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: "'Georgia', serif", letterSpacing: 0.5,
                }}>{b}</button>
              );
            })}
          </div>
        </div>

        {/* SCENE TAGS */}
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: 10, marginBottom: 20,
        }}>
          {SCENE_TAGS.map(tag => {
            const active = activeTag === tag.key;
            return (
              <button key={tag.key} onClick={() => setActiveTag(active ? '' : tag.key)} style={{
                padding: '14px 12px', borderRadius: 8, border: `1px solid ${active ? GOLD : BD}`,
                background: active ? 'rgba(184,137,42,.08)' : '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
              }}>
                <span style={{ fontSize: 18 }}>{tag.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: active ? GOLD : INK }}>{tag.label}</span>
              </button>
            );
          })}
        </div>

        {/* RANKINGS */}
        <div style={{ background: '#fff', borderRadius: 8, border: `1px solid ${BD}`, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 700, color: INK }}>{t('market.rankings')}</span>
          </div>
          <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${BD}`, marginBottom: 12, overflowX: 'auto' }}>
            {RANKING_CATS.map(rc => (
              <button key={rc} onClick={() => setRankCat(rc)} style={{
                padding: '6px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 13, color: rankCat === rc ? GOLD : MUTED, fontWeight: rankCat === rc ? 600 : 400,
                borderBottom: rankCat === rc ? `2px solid ${GOLD}` : '2px solid transparent',
                whiteSpace: 'nowrap',
              }}>{rc}</button>
            ))}
          </div>
          {rankings.length === 0 && <div style={{ textAlign: 'center', color: MUTED, fontSize: 13, padding: 20 }}>{t('common.noData')}</div>}
          {rankings.map((r, i) => (
            <div key={r.id} onClick={() => navigate(`/market/${r.id}`)} style={{
              display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < rankings.length - 1 ? `1px solid ${BD}` : 'none',
              cursor: 'pointer', alignItems: 'center',
            }}>
              <div style={{
                width: 32, fontFamily: 'var(--mono)', fontSize: i < 3 ? 16 : 13,
                fontWeight: 700, color: i < 3 ? GOLD : MUTED,
              }}>
                {i < 3 ? <><span style={{ fontSize: 10, color: GOLD_L }}>TOP</span><br/>{String(i + 1).padStart(2, '0')}</> : String(i + 1).padStart(2, '0')}
              </div>
              <div style={{
                width: 56, height: 56, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
                background: '#F5EDD8', border: `1px solid ${BD}`,
              }}>
                <img src={coverUrl(r)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>{r.brand}</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.item_name}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: GOLD, fontFamily: 'var(--mono)', marginTop: 2 }}>
                  ¥{Number(r.price_cny).toLocaleString()}
                  {r.item_grade && <span style={{
                    marginLeft: 8, fontSize: 10, padding: '1px 5px', borderRadius: 3,
                    background: `${GRADE_COLORS[r.item_grade] || '#999'}15`,
                    color: GRADE_COLORS[r.item_grade] || '#999',
                  }}>{r.item_grade}{t('market.gradeUnit')}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* SORT BAR */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 700, color: INK, marginRight: 8 }}>{t('market.allProducts')}</span>
          {[
            { key: 'newest',     label: t('market.sortComprehensive') },
            { key: 'created',    label: t('market.sortNew') },
            { key: 'price_asc',  label: t('market.sortPriceAsc') },
            { key: 'price_desc', label: t('market.sortPriceDesc') },
          ].map(s => (
            <button key={s.key} onClick={() => setSort(s.key)} style={{
              padding: '5px 12px', borderRadius: 12, border: `1px solid ${sort === s.key ? GOLD : 'transparent'}`,
              background: sort === s.key ? 'rgba(184,137,42,.1)' : 'transparent',
              color: sort === s.key ? GOLD : MUTED, fontSize: 13, cursor: 'pointer', fontWeight: sort === s.key ? 600 : 400,
            }}>{s.label}</button>
          ))}

          {/* Filter button */}
          <button onClick={openFilterModal} style={{
            padding: '5px 12px', borderRadius: 12, border: `1px solid ${activeFilterCount > 0 ? GOLD : 'transparent'}`,
            background: activeFilterCount > 0 ? 'rgba(184,137,42,.1)' : 'transparent',
            color: activeFilterCount > 0 ? GOLD : MUTED, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {t('market.filter')} {activeFilterCount > 0 && <span style={{
              background: GOLD, color: '#fff', borderRadius: '50%', width: 16, height: 16,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
            }}>{activeFilterCount}</span>}
            <span style={{ fontSize: 10 }}>▾</span>
          </button>

          <span style={{ marginLeft: 'auto', fontSize: 12, color: MUTED }}>{total} {t('common.items')}</span>
        </div>

        {/* QUICK FILTER PILLS */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflowX: 'auto', paddingBottom: 2 }}>
          {/* Grade dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={(e) => { e.stopPropagation(); setGradeDropOpen(!gradeDropOpen); setBrandDropOpen(false); }}
              style={chipStyle(activeGrades.length > 0)}>
              {t('market.condition')} {activeGrades.length > 0 ? `(${activeGrades.length})` : ''} ▾
            </button>
            {gradeDropOpen && (
              <div onClick={e => e.stopPropagation()} style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 50,
                background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, padding: 12,
                boxShadow: '0 4px 20px rgba(0,0,0,.1)', minWidth: 200,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {GRADE_OPTIONS.map(g => (
                    <button key={g.key} onClick={() => toggleGrade(g.key)} style={{
                      padding: '8px 12px', borderRadius: 6, textAlign: 'left',
                      border: `1px solid ${activeGrades.includes(g.key) ? GOLD : BD}`,
                      background: activeGrades.includes(g.key) ? 'rgba(184,137,42,.08)' : '#fff',
                      color: activeGrades.includes(g.key) ? GOLD : INK,
                      fontSize: 13, cursor: 'pointer',
                    }}>{g.label}</button>
                  ))}
                </div>
                {activeGrades.length > 0 && (
                  <button onClick={() => setActiveGrades([])} style={{
                    marginTop: 8, width: '100%', padding: '6px 0', border: 'none',
                    background: 'transparent', color: MUTED, fontSize: 12, cursor: 'pointer',
                  }}>{t('market.clearGradeFilter')}</button>
                )}
              </div>
            )}
          </div>

          {/* Brand dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={(e) => { e.stopPropagation(); setBrandDropOpen(!brandDropOpen); setGradeDropOpen(false); }}
              style={chipStyle(!!activeBrand)}>
              {t('market.brand')} {activeBrand ? `· ${activeBrand}` : ''} ▾
            </button>
            {brandDropOpen && (
              <div onClick={e => e.stopPropagation()} style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 50,
                background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, padding: 12,
                boxShadow: '0 4px 20px rgba(0,0,0,.1)', minWidth: 200, maxHeight: 300, overflowY: 'auto',
              }}>
                <button onClick={() => { setActiveBrand(''); setBrandDropOpen(false); }} style={{
                  padding: '8px 12px', borderRadius: 6, width: '100%', textAlign: 'left',
                  border: `1px solid ${!activeBrand ? GOLD : BD}`,
                  background: !activeBrand ? 'rgba(184,137,42,.08)' : '#fff',
                  color: !activeBrand ? GOLD : INK, fontSize: 13, cursor: 'pointer', marginBottom: 4,
                }}>{t('market.allBrands')}</button>
                {brands.map(b => (
                  <button key={b} onClick={() => { setActiveBrand(b); setBrandDropOpen(false); }} style={{
                    padding: '8px 12px', borderRadius: 6, width: '100%', textAlign: 'left',
                    border: `1px solid ${activeBrand === b ? GOLD : BD}`,
                    background: activeBrand === b ? 'rgba(184,137,42,.08)' : '#fff',
                    color: activeBrand === b ? GOLD : INK, fontSize: 13, cursor: 'pointer',
                    fontFamily: "'Georgia', serif", marginBottom: 4,
                  }}>{b}</button>
                ))}
              </div>
            )}
          </div>

          {/* Service quick chips */}
          <button onClick={() => setSourceType(sourceType === 'order' ? '' : 'order')}
            style={chipStyle(sourceType === 'order')}>{t('market.authenticated')}</button>
          <button onClick={() => setPricingMode(pricingMode === 'offer' ? '' : 'offer')}
            style={chipStyle(pricingMode === 'offer')}>{t('market.negotiable')}</button>

          {/* Clear all filters */}
          {activeFilterCount > 0 && (
            <button onClick={clearAllFilters} style={{
              padding: '6px 12px', borderRadius: 16, border: 'none',
              background: 'transparent', color: MUTED, fontSize: 12, cursor: 'pointer',
              textDecoration: 'underline',
            }}>{t('market.clearFilters')}</button>
          )}
        </div>

        {/* Active filter summary */}
        {(priceMin || priceMax) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            padding: '8px 12px', background: 'rgba(184,137,42,.05)', borderRadius: 6,
            fontSize: 12, color: MUTED,
          }}>
            <span>{t('market.priceRangeLabel')}：</span>
            <span style={{ color: GOLD, fontFamily: 'var(--mono)', fontWeight: 500 }}>
              {priceMin ? `¥${Number(priceMin).toLocaleString()}` : t('market.unlimited')}
              {' — '}
              {priceMax ? `¥${Number(priceMax).toLocaleString()}` : t('market.unlimited')}
            </span>
            <button onClick={() => { setPriceMin(''); setPriceMax(''); }} style={{
              marginLeft: 'auto', background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 14,
            }}>×</button>
          </div>
        )}

        {/* PRODUCT GRID */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? 10 : 16,
          paddingBottom: 40,
        }}>
          {items.map(item => (
            <div key={item.id} onClick={() => navigate(`/market/${item.id}`)} style={{
              background: CARD_BG, borderRadius: 8, overflow: 'hidden',
              border: `1px solid ${BD}`, cursor: 'pointer',
              transition: 'box-shadow .2s',
            }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(24,20,12,.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {/* Cover */}
              <div style={{ position: 'relative', paddingTop: '100%', background: '#F5EDD8' }}>
                <img src={coverUrl(item)} alt="" style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover',
                }} />
                {item.exhibition_name && (
                  <div style={{
                    position: 'absolute', top: 8, left: 8, padding: '2px 7px', borderRadius: 3,
                    background: 'rgba(26,107,84,.85)', color: '#fff', fontSize: 10, fontWeight: 600,
                  }}>{t('market.exhibitionTag')}</div>
                )}
                {!item.exhibition_name && item.source_type === 'order' && (
                  <div style={{
                    position: 'absolute', top: 8, left: 8, padding: '2px 7px', borderRadius: 3,
                    background: 'rgba(140,31,31,.85)', color: '#F5E6D0', fontSize: 10, fontWeight: 600,
                  }}>{t('market.auctionSource')}</div>
                )}
                {item.item_grade && (
                  <div style={{
                    position: 'absolute', bottom: 8, right: 8, padding: '2px 6px', borderRadius: 3,
                    background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 10,
                  }}>{item.item_grade}{t('market.gradeUnit')}</div>
                )}
              </div>
              {/* Info */}
              <div style={{ padding: '10px 12px 12px' }}>
                <div style={{ fontSize: 11, color: MUTED, marginBottom: 2, fontFamily: "'Georgia', serif" }}>{item.brand || '—'}</div>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: INK, marginBottom: 6,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{item.item_name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: GOLD, fontFamily: 'var(--mono)' }}>
                    ¥{Number(item.exhibition_price || item.price_cny).toLocaleString()}
                  </span>
                  {item.exhibition_price && Number(item.exhibition_price) < Number(item.price_cny) && (
                    <span style={{ fontSize: 10, color: '#CCC', textDecoration: 'line-through' }}>
                      ¥{Number(item.price_cny).toLocaleString()}
                    </span>
                  )}
                  {item.pricing_mode === 'offer' && !item.exhibition_price && (
                    <span style={{ fontSize: 10, color: MUTED }}>{t('market.negotiable')}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>}
        {!loading && items.length === 0 && <Empty description={t('market.noProducts')} style={{ padding: 60 }} />}

        {/* Load More */}
        {!loading && items.length < total && (
          <div style={{ textAlign: 'center', padding: '0 0 40px' }}>
            <button onClick={() => fetchItems(page + 1)} style={{
              padding: '10px 36px', borderRadius: 20, border: `1px solid ${GOLD}`,
              background: 'transparent', color: GOLD, fontSize: 13, cursor: 'pointer',
            }}>{t('browse.loadMore')}</button>
          </div>
        )}
      </div>

      {/* FILTER MODAL */}
      <Modal
        open={filterOpen}
        onCancel={() => setFilterOpen(false)}
        footer={null}
        title={null}
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 0, maxWidth: '100vw', margin: 0, padding: 0 } : undefined}
        styles={{ body: { padding: 0 } }}
        closable={false}
      >
        <div style={{ padding: '20px 24px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: INK }}>{t('market.allFilters')}</span>
            <button onClick={() => setFilterOpen(false)} style={{
              background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: MUTED,
            }}>×</button>
          </div>

          {/* 品类·品牌 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('market.category')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CATEGORIES.filter(c => c !== '推荐').map(c => (
                <button key={c} onClick={() => { setTmpCat(c); setTmpBrand(''); }}
                  style={filterChipStyle(tmpCat === c)}>{c}</button>
              ))}
              <button onClick={() => { setTmpCat('推荐'); setTmpBrand(''); }}
                style={filterChipStyle(tmpCat === '推荐')}>{t('common.all')}</button>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('market.brand')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 120, overflowY: 'auto' }}>
              <button onClick={() => setTmpBrand('')} style={filterChipStyle(!tmpBrand)}>{t('common.all')}</button>
              {(BRANDS_MAP[tmpCat] || BRANDS_MAP['推荐']).map(b => (
                <button key={b} onClick={() => setTmpBrand(tmpBrand === b ? '' : b)}
                  style={{ ...filterChipStyle(tmpBrand === b), fontFamily: "'Georgia', serif" }}>{b}</button>
              ))}
            </div>
          </div>

          {/* 价格范围 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('market.priceRange')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 14 }}>¥</span>
                <input type="number" value={tmpPriceMin} onChange={e => setTmpPriceMin(e.target.value)}
                  placeholder={t('market.minPrice')}
                  style={{
                    width: '100%', padding: '10px 10px 10px 24px', border: 'none',
                    borderBottom: `1px solid ${BD}`, fontSize: 14, outline: 'none',
                    background: 'transparent', boxSizing: 'border-box',
                  }} />
              </div>
              <span style={{ color: BD, fontSize: 18 }}>—</span>
              <div style={{ flex: 1, position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 14 }}>¥</span>
                <input type="number" value={tmpPriceMax} onChange={e => setTmpPriceMax(e.target.value)}
                  placeholder={t('market.maxPrice')}
                  style={{
                    width: '100%', padding: '10px 10px 10px 24px', border: 'none',
                    borderBottom: `1px solid ${BD}`, fontSize: 14, outline: 'none',
                    background: 'transparent', boxSizing: 'border-box',
                  }} />
              </div>
            </div>
          </div>

          {/* 服务 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('market.service')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <button onClick={() => setTmpSourceType(tmpSourceType === 'order' ? '' : 'order')}
                style={filterChipStyle(tmpSourceType === 'order')}>{t('market.authenticated')}</button>
              <button onClick={() => setTmpPricingMode(tmpPricingMode === 'offer' ? '' : 'offer')}
                style={filterChipStyle(tmpPricingMode === 'offer')}>{t('market.negotiable')}</button>
            </div>
          </div>

          {/* 成色 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: INK, marginBottom: 10 }}>{t('market.condition')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {GRADE_OPTIONS.map(g => (
                <button key={g.key} onClick={() => toggleTmpGrade(g.key)}
                  style={filterChipStyle(tmpGrades.includes(g.key))}>{g.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom buttons */}
        <div style={{
          display: 'flex', gap: 12, padding: '16px 24px',
          borderTop: `1px solid ${BD}`, background: '#fff',
          position: 'sticky', bottom: 0,
        }}>
          <button onClick={resetFilter} style={{
            flex: '0 0 120px', padding: '12px 0', borderRadius: 6,
            border: `1px solid ${BD}`, background: '#fff', color: INK,
            fontSize: 15, cursor: 'pointer',
          }}>{t('common.reset')}</button>
          <button onClick={applyFilter} style={{
            flex: 1, padding: '12px 0', borderRadius: 6, border: 'none',
            background: GOLD, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}>{t('common.confirm')}</button>
        </div>
      </Modal>
    </div>
  );
}
